/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useState } from "react";
import { Flex } from "../../styles/general";
import { COPYRIGHT_COPY } from "../../utils/constants";
import Form, { IFormSignup, SignupFormSection } from "./Form";

const Container = styled.div`
  max-width: 100vw;
  width: 100%;
  min-height: 100%;
  height: 100vh;

  background: linear-gradient(#fff, #fff2e5);

  display: grid;
  grid-template-columns: minmax(720px, 1140px) minmax(640px, 920px);
  align-items: flex-end;
  justify-content: space-between;

  @media screen and (max-width: 1360px) {
    grid-template-columns: 1fr;
  }

  .fake-input {
    opacity: 0;
    position: absolute;
    z-index: -999;
  }
`;

const Illustration = styled.img`
  width: 100%;
  min-height: 100%;
  max-height: 100vh;
  object-fit: cover;

  @media screen and (max-width: 1360px) {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;

    opacity: 0.16;
  }
`;

const Column = styled.div`
  width: 100%;
  min-height: 100%;
  height: 100vh;

  padding: 106px 50px 50px;

  border-right: 1px solid #eeeeee;

  background: #fff;

  overflow-y: auto;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  @media screen and (max-width: 1360px) {
    background: transparent;

    padding: 24px;
  }
`;

const ColumnWrapper = styled.div`
  max-width: 650px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  .title {
    font-size: 48px;
    font-weight: 700;
    color: #222;
    text-align: center;
    margin: 32px 0 8px;

    @media screen and (max-width: 1680px) {
      font-size: 44px;
    }

    @media screen and (max-width: 1440px) {
      font-size: 40px;
    }

    @media screen and (max-width: 1220px) {
      font-size: 36px;
    }

    @media screen and (max-width: 1000px) {
      font-size: 32px;
    }
  }

  .text {
    font-size: 18px;
    font-weight: 400;
    color: #707070;
    text-align: center;

    @media screen and (max-width: 1680px) {
      font-size: 17px;
    }

    @media screen and (max-width: 1220px) {
      font-size: 16px;
    }
  }

  .flex-input {
    width: 100%;
    display: flex;

    > div + div {
      margin-left: 16px;
    }
  }

  .country-code-grid {
    width: 100%;
    display: grid;
    grid-template-columns: auto 80px auto;
    grid-gap: 16px;
  }

  @media screen and (max-width: 1360px) {
    max-width: 650px;
    width: 100%;
    max-height: 538px;

    margin: auto;
    padding: 48px;

    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;

    overflow-y: auto;
    overflow-x: hidden;

    z-index: 3;

    background: #fff;

    border-radius: 8px;
  }
`;

const FooterWrapper = styled.footer`
  margin-top: auto;
  padding-top: 32px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  z-index: 3;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  grid-gap: 8px 16px;
`;

const FooterLink = styled.a`
  color: #707070;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterText = styled.a`
  color: #707070;
  font-size: 14px;
  margin-top: 16px;
`;

const ProgressBar = styled(motion.div)`
  width: 40px;
  height: 9px;
  border-radius: 2px;
  background: #dddddd;
`;

const Logo = styled.img`
  width: 180px;

  position: absolute;
  top: 24px;
  left: 24px;

  @media screen and (max-width: 1360px) {
    width: 240px;
    padding-bottom: 32px;

    position: unset;
    left: unset;
  }
`;

export default function Signup() {
  const [selectedForm, setSelectedForm] = useState(SignupFormSection.Start);

  const [form, setForm] = useState<IFormSignup>({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "1",
    appName: "",
    phone: "",
    domain: "",
    domainSuffix: "",
    sendFromName: "",
    temporaryPasscode: ["", "", "", ""],
    password: ["", "", "", ""],
    confirmPasscode: ["", "", "", ""],
    choosePasscode: ["", "", "", ""],
    origin: "BERSERKER",
    alias: "",
  });

  function getTextPage(selected: SignupFormSection) {
    if (selected === SignupFormSection.Start)
      return "Enter your details below to start your FREE Test Drive.";
    if (selected === SignupFormSection.Create)
      return (
        <div>
          The following details are required for creating your BerserkerMail
          account. <br /> Please ensure you fill out each section correctly, and
          include a valid mobile number.
        </div>
      );
    if (selected === SignupFormSection.TemporyPasscode)
      return "You are just one step away from using our platform and becoming a real Berserker! Please enter the temporary passcode sent to your mobile number.";
    if (selected === SignupFormSection.ChoosePasscode) {
      return "Please choose your new passcode to continue creating your account.";
    }
    if (selectedForm === SignupFormSection.ConfirmPasscode) {
      return "Lastly, please confirm new passcode.";
    }
    if (selectedForm === SignupFormSection.EnterPasscodeLearnistic) {
      return (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <img
            width={70}
            height={70}
            src="./images/learnistic.png"
            alt="learnistic"
          />
          <div
            css={css`
              margin-left: 10px;
              text-align: left;
            `}
          >
            It looks like you already have an existing Learnistic account.
            BerserkerMail and Learnistic are both from the same Learnistic LLC
            company, so you can simply login with your existing Learnistic
            passcode below. If you do not remember your passcode, simply click
            the "Forgot passcode" link below.
          </div>
        </div>
      );
    }
    if (selectedForm === SignupFormSection.TemporyPasscodeLearnistic) {
      return "Please enter the temporary passcode sent to your mobile number.";
    }
    if (selectedForm === SignupFormSection.ChoosePasscodeLearnistic) {
      return "Please choose your new passcode to continue creating your account.";
    }
    if (selectedForm === SignupFormSection.ConfirmPasscodeLearnistic) {
      return "Lastly, please confirm new passcode.";
    }
  }

  function getTitle() {
    if (selectedForm === SignupFormSection.Start) {
      return "Start Your Test Drive";
    }
    if (selectedForm === SignupFormSection.Create) {
      return "Create Account";
    }
    if (selectedForm === SignupFormSection.TemporyPasscode) {
      return "Enter temporary passcode";
    }
    if (selectedForm === SignupFormSection.ChoosePasscode) {
      return "Choose your new passcode";
    }
    if (selectedForm === SignupFormSection.ConfirmPasscode) {
      return "Confirm your new passcode";
    }
    if (selectedForm === SignupFormSection.EnterPasscodeLearnistic) {
      return "Enter your Learnistic passcode";
    }
    if (selectedForm === SignupFormSection.TemporyPasscodeLearnistic) {
      return "Enter temporary passcode";
    }
    if (selectedForm === SignupFormSection.ChoosePasscodeLearnistic) {
      return "Choose your new passcode";
    }
    if (selectedForm === SignupFormSection.ConfirmPasscodeLearnistic) {
      return "Confirm your new passcode";
    }
  }

  return (
    <Container>
      <div className="fake-input">
        <input type="text" />
        <input type="password" />
        <input type="password" />
      </div>
      <Column>
        <Logo src="/icons/login/logo.svg" alt="Logo" />

        <ColumnWrapper>
          <Flex
            css={css`
              margin: 0 auto;
            `}
          >
            <ProgressBar
              css={css`
                margin-right: 10px;
              `}
              transition={{ delay: 0.1, ease: "easeInOut" }}
              animate={{ background: "#FB972E" }}
            />
            <ProgressBar
              css={css`
                margin-right: 10px;
              `}
              transition={{ delay: 0.1, ease: "easeInOut" }}
              animate={
                selectedForm !== SignupFormSection.Start
                  ? { background: "#FB972E" }
                  : { background: "#dddddd" }
              }
            />
            <ProgressBar
              transition={{ delay: 0.1, ease: "easeInOut" }}
              animate={
                selectedForm !== SignupFormSection.Start &&
                selectedForm !== SignupFormSection.Create
                  ? { background: "#FB972E" }
                  : { background: "#dddddd" }
              }
            />
          </Flex>

          <div className="title">{getTitle()}</div>
          <div className="text">{getTextPage(selectedForm)}</div>

          <Form
            selectedForm={selectedForm}
            setSelectedForm={setSelectedForm}
            form={form}
            setForm={setForm}
          />
        </ColumnWrapper>

        <Footer />
      </Column>
      <Illustration src="./images/LoginImage.svg" alt="Illustration" />
    </Container>
  );
}

function Footer() {
  return (
    <FooterWrapper>
      <FooterLinks>
        <FooterLink href="https://berserkermail.com/pp" target="_blank">
          Privacy Policy
        </FooterLink>
        <FooterLink href="https://berserkermail.com/tos" target="_blank">
          Terms of Service
        </FooterLink>
        <FooterLink
          href="https://berserkermail.com/subscription_agreement"
          target="_blank"
        >
          Subscription Agreement
        </FooterLink>
        <FooterLink
          href="https://berserkermail.com/guidelines.html"
          target="_blank"
        >
          BerserkerMail Guidelines
        </FooterLink>
        <FooterLink
          href="https://berserkermail.com/cancellation.html"
          target="_blank"
        >
          Refunds & Cancellations Policy
        </FooterLink>
        <FooterLink href="https://berserkermail.com/contact" target="_blank">
          Contact Us
        </FooterLink>
      </FooterLinks>

      <FooterText dangerouslySetInnerHTML={{ __html: COPYRIGHT_COPY }} />
    </FooterWrapper>
  );
}
